/**
 * Comments section for a requirement.
 * Supports rich text (images, links) via Quill editor.
 * Own comments are editable/deletable; others are read-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography, Space, Popconfirm, message, Spin, Avatar } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResizeImage from 'quill-resize-image';
import { useAuthStore } from '@/store/authStore';
import { listComments, createComment, deleteComment } from '@/api/requirements';
import { getUploadUrl } from '@/api/storage';
import { getAccessToken } from '@/api/client';
import type { RequirementComment } from '@/types';
import { useT, t as tGlobal } from '@/i18n';

// Register resize module with Quill
Quill.register('modules/resize', QuillResizeImage);

const { Text } = Typography;

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Build image proxy URL with auth token for <img> tags */
function imageProxyUrl(objectKey: string): string {
  const token = getAccessToken();
  return `${API_BASE}/storage/image/${objectKey}?token=${token}`;
}

/** Format relative time, e.g. "3 天前" / "3 days ago" */
function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return tGlobal('刚刚');
  if (minutes < 60) return tGlobal('{n} 分钟前', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tGlobal('{n} 小时前', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return tGlobal('{n} 天前', { n: days });
  const months = Math.floor(days / 30);
  if (months < 12) return tGlobal('{n} 个月前', { n: months });
  return tGlobal('{n} 年前', { n: Math.floor(months / 12) });
}

/** Check if HTML content is effectively empty (text or images) */
function isContentEmpty(html: string): boolean {
  if (/<img\s/i.test(html)) return false;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
}

/** Refresh image proxy tokens in stored HTML for display */
function refreshImageTokens(html: string): string {
  const imagePattern = new RegExp(
    `${API_BASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/storage/image/([^"?]+)(\\?[^"]*)?`,
    'g'
  );
  return html.replace(imagePattern, (_match, objectKey) => imageProxyUrl(objectKey));
}

/** Upload an image file to OBS and return the image proxy URL */
async function uploadImage(file: File): Promise<string> {
  const { url, object_key } = await getUploadUrl(file.name, file.type);
  await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  return imageProxyUrl(object_key);
}

/**
 * Find a base64 image in editor content and replace it with an uploaded URL.
 * Quill inserts base64 when user pastes — we detect it via text-change and
 * asynchronously upload + swap the src.
 */
async function replaceBase64Image(editor: ReturnType<ReactQuill['getEditor']>, base64Src: string) {
  try {
    // Convert base64 data URL to File
    const res = await fetch(base64Src);
    const blob = await res.blob();
    const ext = blob.type.split('/')[1] || 'png';
    const file = new File([blob], `paste-${Date.now()}.${ext}`, { type: blob.type });

    const proxyUrl = await uploadImage(file);

    // Walk the editor delta to find and replace the base64 image
    const contents = editor.getContents();
    let index = 0;
    for (const op of contents.ops ?? []) {
      if (typeof op.insert === 'string') {
        index += op.insert.length;
      } else if (typeof op.insert === 'object') {
        if ((op.insert as { image?: string }).image === base64Src) {
          editor.deleteText(index, 1);
          editor.insertEmbed(index, 'image', proxyUrl);
          break;
        }
        index += 1;
      }
    }
  } catch {
    message.error(tGlobal('图片上传失败'));
  }
}

const getQuillModules = (t: (key: string, vars?: Record<string, string | number>) => string) => ({
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link', 'image'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
  resize: {
    locale: {
      altTip: t('按住alt键比例缩放'),
      inputTip: t('输入大小后按回车确认'),
    },
  },
});

const QUILL_FORMATS = [
  'bold', 'italic', 'underline',
  'link', 'image',
  'list',
];

interface RequirementCommentsProps {
  projectId: number;
  requirementId: string;
}

const RequirementComments: React.FC<RequirementCommentsProps> = ({
  projectId,
  requirementId,
}) => {
  const { t } = useT();
  const { user } = useAuthStore();
  const [comments, setComments] = useState<RequirementComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const newQuillRef = useRef<ReactQuill | null>(null);
  const editQuillRef = useRef<ReactQuill | null>(null);
  const pendingUploadsRef = useRef(0);
  const [uploading, setUploading] = useState(false);

  /** Set up a Quill editor: toolbar image handler + base64 auto-upload */
  const setupEditor = useCallback((quillRef: React.RefObject<ReactQuill | null>) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Toolbar image button → file picker → upload to OBS
    const toolbar = editor.getModule('toolbar') as { addHandler: (type: string, handler: () => void) => void };
    toolbar.addHandler('image', () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const proxyUrl = await uploadImage(file);
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', proxyUrl);
          editor.setSelection(range.index + 1);
        } catch {
          message.error(t('图片上传失败'));
        }
      };
    });

    // Detect base64 images inserted by Quill's clipboard (paste/drop)
    // and replace them with uploaded OBS URLs
    editor.on('text-change', (delta: { ops?: Array<{ insert?: string | { image?: string } }> }) => {
      for (const op of delta.ops ?? []) {
        if (typeof op.insert === 'object' && op.insert !== null) {
          const img = (op.insert as { image?: string }).image;
          if (img && img.startsWith('data:')) {
            // Async: upload the base64 image and replace in-place
            pendingUploadsRef.current += 1;
            setUploading(true);
            replaceBase64Image(editor, img).finally(() => {
              pendingUploadsRef.current -= 1;
              if (pendingUploadsRef.current <= 0) {
                pendingUploadsRef.current = 0;
                setUploading(false);
              }
            });
          }
        }
      }
    });
  }, [t]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const data = await listComments(projectId, requirementId);
      setComments(data);
    } catch {
      message.error(t('加载评论失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [projectId, requirementId]);

  // Set up editors after mount
  useEffect(() => {
    const timer = setTimeout(() => setupEditor(newQuillRef), 100);
    return () => clearTimeout(timer);
  }, [setupEditor]);

  useEffect(() => {
    if (editingId !== null) {
      const timer = setTimeout(() => setupEditor(editQuillRef), 100);
      return () => clearTimeout(timer);
    }
  }, [editingId, setupEditor]);

  const handleSubmit = async () => {
    if (isContentEmpty(newContent)) return;
    if (uploading || /src="data:image\//i.test(newContent)) {
      message.warning(t('图片上传中，请稍候...'));
      return;
    }
    setSubmitting(true);
    try {
      const comment = await createComment(projectId, requirementId, {
        content: newContent,
      });
      setComments([comment, ...comments]);
      setNewContent('');
    } catch {
      message.error(t('发送评论失败'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    try {
      await deleteComment(projectId, requirementId, commentId);
      setComments(comments.filter((c) => c.id !== commentId));
      message.success(t('评论已删除'));
    } catch {
      message.error(t('删除评论失败'));
    }
  };

  const startEdit = (item: RequirementComment) => {
    setEditingId(item.id);
    setEditContent(item.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async () => {
    if (isContentEmpty(editContent) || editingId === null) return;
    if (uploading || /src="data:image\//i.test(editContent)) {
      message.warning(t('图片上传中，请稍候...'));
      return;
    }
    try {
      await deleteComment(projectId, requirementId, editingId);
      const comment = await createComment(projectId, requirementId, {
        content: editContent,
      });
      setComments(
        comments.map((c) => (c.id === editingId ? comment : c))
      );
      setEditingId(null);
      setEditContent('');
      message.success(t('评论已更新'));
    } catch {
      message.error(t('更新评论失败'));
    }
  };

  const isOwn = (item: RequirementComment) =>
    user && item.created_by?.id === Number(user.id);

  const displayName = (item: RequirementComment) =>
    item.created_by?.display_name || item.created_by?.username || t('未知用户');

  const editorModules = useMemo(() => getQuillModules(t), [t]);

  return (
    <div>
      {/* New comment input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Avatar size="small" icon={<UserOutlined />} style={{ flexShrink: 0, marginTop: 4 }} />
        <div style={{ flex: 1 }}>
          <ReactQuill
            ref={newQuillRef}
            theme="snow"
            value={newContent}
            onChange={setNewContent}
            modules={editorModules}
            formats={QUILL_FORMATS}
            placeholder={t('输入评论... 支持粘贴图片和超链接')}
            style={{ background: '#fff' }}
          />
          <Button
            type="primary"
            size="small"
            onClick={handleSubmit}
            loading={submitting}
            disabled={isContentEmpty(newContent) || uploading}
            style={{ marginTop: 8 }}
          >
            {uploading ? t('图片上传中...') : t('发送')}
          </Button>
        </div>
      </div>

      {/* Comment list */}
      {loading ? (
        <Spin size="small" />
      ) : comments.length === 0 ? (
        <Text type="secondary">{t('暂无评论')}</Text>
      ) : (
        <div>
          {comments.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                gap: 8,
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Avatar size="small" icon={<UserOutlined />} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header: name + time + actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 13 }}>{displayName(item)}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    added a comment - {relativeTime(item.created_at)}
                  </Text>
                  {isOwn(item) && editingId !== item.id && (
                    <Space size={4} style={{ marginLeft: 'auto' }}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => startEdit(item)}
                      />
                      <Popconfirm title={t('确认删除？')} onConfirm={() => handleDelete(item.id)}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  )}
                </div>

                {/* Content or inline editor */}
                {editingId === item.id ? (
                  <div>
                    <ReactQuill
                      ref={editQuillRef}
                      theme="snow"
                      value={editContent}
                      onChange={setEditContent}
                      modules={editorModules}
                      formats={QUILL_FORMATS}
                      style={{ background: '#fff' }}
                    />
                    <Space size={4} style={{ marginTop: 4 }}>
                      <Button size="small" type="primary" icon={<CheckOutlined />} onClick={saveEdit}>
                        {t('保存')}
                      </Button>
                      <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>
                        {t('取消')}
                      </Button>
                    </Space>
                  </div>
                ) : (
                  <div
                    className="comment-content ql-editor"
                    style={{ margin: 0, fontSize: 13, padding: 0 }}
                    dangerouslySetInnerHTML={{ __html: refreshImageTokens(item.content) }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequirementComments;
